import { ImportedListing } from '../../_types/imported-listing'
import { CompareVerdictWinner } from '../../_types/quickpass-llm'
import { CompareVerdictCacheEntry } from './types'

const CACHE_PREFIX = 'quickpass.compareVerdict:v1:'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000
const DESCRIPTION_EXCERPT_MAX = 320

const compactListing = (listing: ImportedListing): Record<string, unknown> => {
  const trimmedDescription = listing.description
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, DESCRIPTION_EXCERPT_MAX)

  return {
    id: listing.id,
    title: listing.title,
    priceMonthly: listing.priceMonthly,
    bedrooms: listing.bedrooms,
    bathrooms: listing.bathrooms,
    propertyType: listing.propertyType,
    city: listing.city,
    fullAddress: listing.fullAddress,
    isAvailable: listing.isAvailable,
    availableDate: listing.availableDate,
    descriptionExcerpt: trimmedDescription,
  }
}

/**
 * Builds a stable cache key for a compare-verdict pair. Using
 * sorted IDs means alpha+beta and beta+alpha share the same cache —
 * but the LLM still sees them in their original orientation.
 *
 * @param {string} alphaId
 * @param {string} betaId
 * @returns {string} the sorted-pair cache key
 */
const buildPairKey = (alphaId: string, betaId: string): string => {
  const sorted = [alphaId, betaId].sort()
  return `${sorted[0]}::${sorted[1]}`
}

const readCache = (pairKey: string): CompareVerdictCacheEntry | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${pairKey}`)
    if (raw === null) {
      return null
    }
    const parsed = JSON.parse(raw) as CompareVerdictCacheEntry
    if (Date.now() - parsed.generatedAt > CACHE_TTL_MS) {
      localStorage.removeItem(`${CACHE_PREFIX}${pairKey}`)
      return null
    }
    return parsed
  } catch (error) {
    console.error('[compare-verdict] cache read failed', error)
    return null
  }
}

const writeCache = (entry: CompareVerdictCacheEntry): void => {
  try {
    localStorage.setItem(
      `${CACHE_PREFIX}${entry.pairKey}`,
      JSON.stringify(entry),
    )
  } catch (error) {
    console.error('[compare-verdict] cache write failed', error)
  }
}

const winnerLabelFor = (
  winner: CompareVerdictWinner,
  alphaTitle: string,
  betaTitle: string,
): string => {
  if (winner === 'alpha') {
    return alphaTitle
  }
  if (winner === 'beta') {
    return betaTitle
  }
  return "It's essentially a tie"
}

export { compactListing, buildPairKey, readCache, writeCache, winnerLabelFor }
